import type { Answers, AnswerValue, Catalogue, Condition, Field } from './catalogue';

/**
 * Completion, conditions and validation, read from the catalogue.
 *
 * The same rules run on the server, in backend `intake/Rules.java`, which decides
 * what counts at submission. This copy draws the stamps and the review banner
 * without a round trip. Both are held to
 * backend/src/test/resources/intake/rule-cases.json; change a case there before
 * changing either, or the stamps will disagree with the server.
 *
 * Pure: "today" is passed in as an ISO date, so date rules test on any day.
 */

export type Severity = 'ERROR' | 'WARNING';

/** Codes, not sentences: `issueMessage` words them. */
export type IssueCode =
  | 'invalid_email'
  | 'invalid_phone'
  | 'invalid_date'
  | 'date_in_future'
  | 'date_not_future'
  | 'date_not_after'
  | 'expires_soon';

export interface Issue {
  field: string;
  code: IssueCode;
  severity: Severity;
}

export interface SectionStatus {
  id: string;
  complete: boolean;
  missing: string[];
}

export interface Evaluation {
  sections: SectionStatus[];
  issues: Issue[];
  highlightedDocuments: string[];
}

// Deliberately loose: whether mail arrives is the real check. This catches the typo.
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_SEPARATORS = /[\s\-.()]/g;
const E164 = /^\+[1-9]\d{7,14}$/;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function evaluate(catalogue: Catalogue, answers: Answers, today: string): Evaluation {
  const issues: Issue[] = [];
  const sections = catalogue.sections.map((section) => {
    const missing: string[] = [];
    for (const field of section.fields) {
      if (!applies(field, answers)) continue;
      const value = answers[field.id];
      const answered = isAnswered(field, value);
      if (isRequired(field, answers) && !answered) missing.push(field.id);
      if (answered) check(field, value!, answers, today, issues);
    }
    return { id: section.id, complete: missing.length === 0, missing };
  });
  const highlightedDocuments = catalogue.documents
    .filter((d) => d.highlightWhen !== undefined && holds(d.highlightWhen, answers))
    .map((d) => d.id);
  return { sections, issues, highlightedDocuments };
}

/** Whether the field is shown at all. One that does not apply is ignored. */
export function applies(field: Field, answers: Answers): boolean {
  return field.when === undefined || holds(field.when, answers);
}

export function isRequired(field: Field, answers: Answers): boolean {
  return (
    applies(field, answers) &&
    (field.required || (field.requiredWhen !== undefined && holds(field.requiredWhen, answers)))
  );
}

function holds(condition: Condition, answers: Answers): boolean {
  const value = answers[condition.field];
  if (condition.answered === true) return hasValue(value);
  const wanted = condition.in ?? [];
  if (Array.isArray(value)) return value.some((item) => wanted.includes(item));
  return typeof value === 'string' && wanted.includes(value);
}

export function isAnswered(field: Field, value: AnswerValue | undefined): boolean {
  if (value === undefined) return false;
  switch (field.type) {
    case 'multi':
      return Array.isArray(value) && value.length > 0;
    case 'yesno':
      return value === 'yes' || value === 'no';
    default:
      return typeof value === 'string' && value.trim() !== '';
  }
}

function hasValue(value: AnswerValue | undefined): boolean {
  if (Array.isArray(value)) return value.length > 0;
  return typeof value === 'string' && value.trim() !== '';
}

function check(field: Field, value: AnswerValue, answers: Answers, today: string, issues: Issue[]): void {
  const text = typeof value === 'string' ? value.trim() : '';
  if (field.format === 'email' && !EMAIL.test(text)) {
    issues.push({ field: field.id, code: 'invalid_email', severity: 'ERROR' });
  }
  if (field.format === 'phone' && !E164.test(normalisePhone(text))) {
    issues.push({ field: field.id, code: 'invalid_phone', severity: 'ERROR' });
  }
  if (field.type !== 'date') return;
  if (!isValidDate(text)) {
    issues.push({ field: field.id, code: 'invalid_date', severity: 'ERROR' });
    return;
  }
  // ISO dates compare correctly as strings once known to be valid.
  if (field.notFuture === true && text > today) {
    issues.push({ field: field.id, code: 'date_in_future', severity: 'ERROR' });
  }
  if (field.future === true && text <= today) {
    issues.push({ field: field.id, code: 'date_not_future', severity: 'ERROR' });
  } else if (field.warnWithinMonths !== undefined && text < addMonths(today, field.warnWithinMonths)) {
    issues.push({ field: field.id, code: 'expires_soon', severity: 'WARNING' });
  }
  if (field.after !== undefined) {
    const other = answers[field.after];
    const earlier = typeof other === 'string' ? other.trim() : '';
    if (isValidDate(earlier) && text <= earlier) {
      issues.push({ field: field.id, code: 'date_not_after', severity: 'ERROR' });
    }
  }
}

/** Spaces, hyphens, dots and brackets removed: how people write numbers, not part of them. */
export function normalisePhone(phone: string): string {
  return phone.replace(PHONE_SEPARATORS, '');
}

function daysIn(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export function isValidDate(text: string): boolean {
  const match = ISO_DATE.exec(text);
  if (!match) return false;
  const [, y, m, d] = match.map(Number) as [number, number, number, number];
  return m >= 1 && m <= 12 && d >= 1 && d <= daysIn(y, m);
}

/**
 * Calendar months, clamped to the end of a shorter month, as Java's
 * LocalDate.plusMonths does: Aug 31 plus six months is Feb 28, not Mar 3, which is
 * what Date's own arithmetic would give.
 */
export function addMonths(iso: string, months: number): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  const total = y * 12 + (m - 1) + months;
  const year = Math.floor(total / 12);
  const month = (total % 12) + 1;
  const day = Math.min(d, daysIn(year, month));
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** The sentence under a field. One place, so the summary and the form agree. */
export function issueMessage(issue: Issue): string {
  switch (issue.code) {
    case 'invalid_email':
      return 'Enter a valid email address, such as name@example.com.';
    case 'invalid_phone':
      return 'Enter the number with its country code, such as +254 712 345 678.';
    case 'invalid_date':
      return 'Enter a real date.';
    case 'date_in_future':
      return 'This date cannot be in the future.';
    case 'date_not_future':
      return 'This passport has expired. Enter the details of a current passport.';
    case 'date_not_after':
      return 'The expiry date must be after the issue date.';
    case 'expires_soon':
      return 'This passport expires within six months. Regulators may ask for a renewed one.';
  }
}

/** Today in the browser's own calendar, as an ISO date: what the applicant calls today. */
export function todayIso(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}
